class SizeChartModal {
  constructor() {
    this.modal = document.querySelector('.ctm_prd_size_modal');
    this.tableBody = document.getElementById('sizeTableBody');
    this.unitDisplay = document.querySelector('.unit-display');
    this.tableHead = document.querySelector('.ctm_prd_size_table thead');
    this.currentUnit = 'in'; 
    this.sizeData = null;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Handle all open buttons (multiple instances support)
    document.addEventListener('click', (e) => {
      const sizeBtn = e.target.closest('.ctm_prd_size_btn');
      if (sizeBtn) {
        e.preventDefault();
        this.extractSizeData(sizeBtn);
        this.openModal();
      }
    });

    // Close button
    const closeBtn = document.querySelector('.ctm_prd_size_modal_close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal());
    }

    // Click outside to close
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.closeModal();
        }
      });
    }

    // Unit toggle buttons
    const unitButtons = document.querySelectorAll('.ctm_prd_size_unit_btn');
    unitButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        unitButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.currentUnit = btn.dataset.unit;
        this.updateTable();
      });
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.closeModal();
      }
    });
  }

  extractSizeData(sizeBtnElement) {
    try {
      const sizeScript = sizeBtnElement.querySelector('script[data-jsonprd-size]');
      
      if (!sizeScript) {
        console.warn('No size data script found in the size button');
        this.sizeData = { in: [], cm: [] };
        return;
      }

      const scriptContent = sizeScript.textContent.trim();
      
      const sizeGuideMatch = scriptContent.match(/const sizeGuide = ({[\s\S]*?});/);
      
      if (sizeGuideMatch && sizeGuideMatch[1]) {
        let sizeGuideJson = sizeGuideMatch[1];
        
        // Fix common JSON issues - remove trailing commas
        sizeGuideJson = sizeGuideJson.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');
        
        try {
          const sizeGuide = JSON.parse(sizeGuideJson);
          
          this.sizeData = {
            in: sizeGuide.inch || sizeGuide.in || [],
            cm: sizeGuide.cm || []
          };
          
        } catch (parseError) {
          console.error('Error parsing sizeGuide JSON:', parseError);
          try {
            const getSizeGuide = new Function(scriptContent + ' return sizeGuide;');
            const sizeGuide = getSizeGuide();
            this.sizeData = {
              in: sizeGuide.inch || sizeGuide.in || [],
              cm: sizeGuide.cm || []
            };
          } catch (evalError) {
            console.error('Error evaluating sizeGuide:', evalError);
            this.sizeData = { in: [], cm: [] };
          }
        }
      } else {
        console.warn('Could not extract sizeGuide object from script');
        this.sizeData = { in: [], cm: [] };
      }
    } catch (error) {
      console.error('Error extracting size data:', error);
      this.sizeData = { in: [], cm: [] };
    }
  }

  isOpen() {
    return this.modal && this.modal.classList.contains('active');
  }

  openModal() {
    if (!this.modal) {
      console.error('Size chart modal not found');
      return;
    }
    
    if (!this.sizeData) {
      console.error('No size data available');
      return;
    }

    // Update table with extracted data
    this.updateTable();
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  updateTable() {
    if (!this.tableBody || !this.unitDisplay || !this.tableHead) {
      console.error('Required DOM elements not found');
      return;
    }

    if (!this.sizeData) {
      console.error('No size data available for table');
      this.showEmptyState();
      return;
    }

    const data = this.sizeData[this.currentUnit];
    
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.error('Invalid data format for unit:', this.currentUnit, 'Available keys:', Object.keys(this.sizeData));
      this.showEmptyState();
      return;
    }

    // Get available fields from the first data item (excluding 'size')
    const firstItem = data[0];
    const measurementFields = Object.keys(firstItem).filter(key => key !== 'size');
    
    // Update table headers based on available fields
    this.updateTableHeaders(measurementFields);
    
    // Update table body
    this.updateTableBody(data, measurementFields);
  }

  updateTableHeaders(measurementFields) {
    // Update unit display
    this.unitDisplay.textContent = this.currentUnit.toUpperCase();
    
    // Create header row
    const headerRow = document.createElement('tr');
    
    // Add SIZE header
    const sizeHeader = document.createElement('th');
    sizeHeader.textContent = 'SIZE';
    headerRow.appendChild(sizeHeader);
    
    // Add measurement headers based on available fields
    measurementFields.forEach(field => {
      const header = document.createElement('th');
      header.textContent = this.formatHeaderText(field);
      headerRow.appendChild(header);
    });
    
    // Replace existing header content
    this.tableHead.innerHTML = '';
    this.tableHead.appendChild(headerRow);
  }

  updateTableBody(data, measurementFields) {
    this.tableBody.innerHTML = '';

    data.forEach(item => {
      const row = document.createElement('tr');
      
      // Add size cell
      const sizeCell = document.createElement('td');
      sizeCell.textContent = item.size || '';
      row.appendChild(sizeCell);
      
      // Add measurement cells based on available fields
      measurementFields.forEach(field => {
        const cell = document.createElement('td');
        cell.textContent = item[field] || '';
        row.appendChild(cell);
      });
      
      this.tableBody.appendChild(row);
    });
  }

  formatHeaderText(field) {
    // Convert field names to readable header text
    const headerMap = {
      'shoulder': 'SHOULDER',
      'length': 'LENGTH',
      'chest': 'CHEST',
      'sleeve': 'SLEEVE',
      'sleeve_length': 'SLEEVE LENGTH'
    };
    
    return headerMap[field] || field.toUpperCase();
  }

  showEmptyState() {
    if (!this.tableBody) return;
    this.tableBody.innerHTML = `
      <tr>
        <td colspan="5" style="text-align: center; padding: 20px;">
          No size data available for this product
        </td>
      </tr>
    `;
  }
}

class ProductVariantSelector {
  constructor(container) {
    this.container = container;
    this.productId = this.container.dataset.productId; // Get unique product ID
    this.tray = this.container.querySelector('.variant-tray');
    this.trigger = this.container.querySelector('.prd_card_quick_view_btn');
    this.closeBtn = this.container.querySelector('.variant-tray__close');
    this.sectionIdsToRefresh = ['cart-drawer', 'cart-counter'];

    this.init();
  }

  init() {
    this.elements = {
      variantButtons: this.container.querySelectorAll('.button--variant'),
      selectSizeBtn: this.container.querySelector('.select-size-btn'),
      productActions: this.container.querySelector('.product-actions'),
      addToBagBtn: this.container.querySelector('.add-to-bag'),
      buyNowBtn: this.container.querySelector('.buy-now')
    };

    // Clear any preselected variants
    this.clearPreselectedVariants();
    
    this.bindOpenClose();
    this.bindEvents();
    this.checkInitialState();
  }

  clearPreselectedVariants() {
    // Uncheck all radio inputs in this container
    const radioInputs = this.container.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.checked = false;
    });
    
    // Remove selected class from all variant buttons
    this.elements.variantButtons.forEach(btn => {
      btn.classList.remove('selected');
    });
  }

  bindOpenClose() {
    if (this.trigger) {
      this.trigger.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent event bubbling
        this.openTray();
      });
    }
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent event bubbling
        this.closeTray();
      });
    }
    
    // Scoped escape key handler
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.closeTray();
    });
    
    // Scoped click outside handler
    document.addEventListener('click', (e) => {
      if (!this.isOpen()) return;
      const clickedInside = this.tray.contains(e.target) || this.trigger.contains(e.target);
      if (!clickedInside) this.closeTray();
    });
  }

  isOpen() {
    return this.tray && this.tray.classList.contains('is-open');
  }

  openTray() {
    if (!this.tray) return;
    
    // Close all other trays first
    document.querySelectorAll('.variant-tray.is-open').forEach(otherTray => {
      if (otherTray !== this.tray) {
        otherTray.classList.remove('is-open');
        const otherContainer = otherTray.closest('.product_card_var_options');
        if (otherContainer) {
          otherContainer.classList.remove('tray_is_open');
        }
      }
    });
    
    this.tray.classList.add('is-open');
    this.container.classList.add('tray_is_open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');

    const focusable = this.tray.querySelector('.button--variant, .select-size-btn, .add-to-bag, .buy-now, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus();
  }

  closeTray() {
    if (!this.tray) return;
    this.tray.classList.remove('is-open');
    this.container.classList.remove('tray_is_open');
    if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    if (this.trigger) this.trigger.focus();
  }

  bindEvents() {
    this.elements.variantButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent event bubbling
        this.handleVariantClick(button);
      });
      button.addEventListener('keypress', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation(); // Prevent event bubbling
          this.handleVariantClick(button);
        }
      });
    });

    if (this.elements.addToBagBtn) {
      this.elements.addToBagBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent event bubbling
        this.handleAddToBag();
      });
    }

    if (this.elements.buyNowBtn) {
      this.elements.buyNowBtn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent event bubbling
        this.handleBuyNow();
      });
    }
  }

  checkInitialState() {
    // Check if any variant is selected in THIS container only
    const selectedButton = this.container.querySelector('.button--variant input:checked');
    const selectedButtonParent = selectedButton ? selectedButton.closest('.button--variant') : null;
    
    if (selectedButtonParent) {
      selectedButtonParent.classList.add('selected');
      this.showActionButtons();
    } else {
      this.showSelectSizeButton();
    }
  }

  handleVariantClick(button) {
    if (button.classList.contains('out-of-stock')) return;

    // Only affect buttons in THIS container
    this.elements.variantButtons.forEach(btn => btn.classList.remove('selected'));
    button.classList.add('selected');

    const radioInput = button.querySelector('input[type="radio"]');
    if (radioInput) {
      radioInput.checked = true;
      button.setAttribute('aria-live', 'polite');
    }

    this.showActionButtons();
  }

  showSelectSizeButton() {
    if (this.elements.selectSizeBtn) this.elements.selectSizeBtn.classList.remove('hidden');
    if (this.elements.productActions) this.elements.productActions.classList.add('hidden');
  }

  showActionButtons() {
    if (this.elements.selectSizeBtn) this.elements.selectSizeBtn.classList.add('hidden');
    if (this.elements.productActions) this.elements.productActions.classList.remove('hidden');
  }

  handleAddToBag() {
    const selectedButton = this.container.querySelector('.button--variant.selected');
    if (!selectedButton) {
      alert('Please select a size');
      return;
    }
    const radioInput = selectedButton.querySelector('input[type="radio"]');
    const variantId = radioInput ? radioInput.dataset.variantId : null;
    if (!variantId) {
      alert('This variant is not available');
      return;
    }
    this.addToCart(variantId, 1);
  }

  handleBuyNow() {
    const selectedButton = this.container.querySelector('.button--variant.selected');
    if (!selectedButton) {
      alert('Please select a size');
      return;
    }
    const radioInput = selectedButton.querySelector('input[type="radio"]');
    const variantId = radioInput ? radioInput.dataset.variantId : null;
    if (!variantId) {
      alert('This variant is not available');
      return;
    }
    this.addToCart(variantId, 1, true);
  }

  async updateCartDrawer() {
    try {
      const res = await fetch(`${window.routes?.cart_url || '/cart'}?sections=${this.sectionIdsToRefresh.join(',')}`);
      const sections = await res.json();

      this.sectionIdsToRefresh.forEach((sectionId) => {
        const htmlString = sections[sectionId];
        if (!htmlString) return;

        const html = new DOMParser().parseFromString(htmlString, 'text/html');

        if (sectionId === 'cart-drawer') {
          const sourceInner = html.querySelector('.cart-drawer__inner');
          const targetInner = document.querySelector('.cart-drawer__inner');
          if (sourceInner && targetInner) {
            targetInner.innerHTML = sourceInner.innerHTML;
          }
        }

        if (sectionId === 'cart-counter') {
          const sourceCounter = html.querySelector('#shopify-section-cart-counter');
          const targetCounter = document.querySelector('#cart-counter .header__utils-link--icon');
          if (sourceCounter && targetCounter) {
            targetCounter.innerHTML = sourceCounter.innerHTML;
          }
        }
      });

      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else if (cartDrawer) {
        cartDrawer.classList.add('active');
        cartDrawer.classList.remove('is-empty');
        document.body.classList.add('overflow-hidden');
      }
    } catch (e) {
      console.error('Error updating cart sections:', e);
    }
  }

  async addToCart(variantId, quantity, redirectToCheckout = false) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.description || 'Failed to add to cart');
      }

      await this.updateCartDrawer();

      if (redirectToCheckout) {
        window.location.href = '/checkout';
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Failed to add to cart. Please try again.');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Initialize Size Chart Modal (single instance)
  new SizeChartModal();

  // Initialize Product Variant Selectors (multiple instances)
  const productContainers = document.querySelectorAll('.product_card_var_options');
  productContainers.forEach(container => new ProductVariantSelector(container));
});



document.addEventListener('custom_size_init', function (event) {
  new SizeChartModal();
  const productContainers = document.querySelectorAll('.product_card_var_options');
  productContainers.forEach(container => new ProductVariantSelector(container));
});


// Mian Product slider css
(() => {
  const BREAKPOINT = '(min-width: 992px)'; // desktop only
  const STEP = 360;                         // px per click
  const HOLD_SPEED = 28;                    // px per tick
  const HOLD_INTERVAL = 16;                 // ms
  const RAIL_INSET = 14;                    // must match CSS ::before top/bottom

  const rail  = document.querySelector('.ctm_desk_main_slider');
  const up    = document.querySelector('.ctm-arrow--up');
  const down  = document.querySelector('.ctm-arrow--down');
  const track = document.querySelector('.ctm-vbar__track');
  const thumb = document.querySelector('.ctm-vbar__thumb');
  if (!rail || !up || !down || !track || !thumb) return;

  const mql = window.matchMedia(BREAKPOINT);
  let mounted = false, holdTimer = null, dragging = false, dragStartY = 0, dragStartScroll = 0, ro = null;

  const clamp = (v,a,b)=>Math.max(a,Math.min(b,v));
  const atTop = ()=> rail.scrollTop <= 0;
  const atBottom = ()=> rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 1;

  function setDisabled(){ up.disabled = atTop(); down.disabled = atBottom(); }

  function syncThumb(){
    const usable = track.clientHeight;                         // ~200
    const innerUsable = Math.max(usable - RAIL_INSET*2, 0);    // travel area within rail
    const ratio  = rail.clientHeight / Math.max(rail.scrollHeight, 1);
    const minThumb = 36;
    const h = clamp(Math.round(innerUsable * ratio), Math.min(minThumb, innerUsable), innerUsable);
    thumb.style.height = h + 'px';

    const maxScroll = Math.max(rail.scrollHeight - rail.clientHeight, 0);
    const maxY = Math.max(innerUsable - h, 0);
    const y = maxScroll ? (rail.scrollTop / maxScroll) * maxY : 0;

    thumb.style.transform = `translate(-50%, ${RAIL_INSET + y}px)`; // offset by inset
    setDisabled();
  }

  function scrollBy(delta, behavior='smooth'){
    const max = rail.scrollHeight - rail.clientHeight;
    rail.scrollTo({ top: clamp(rail.scrollTop + delta, 0, max), behavior });
  }

  function startHold(dir){
    stopHold();
    holdTimer = setInterval(() => {
      if ((dir < 0 && atTop()) || (dir > 0 && atBottom())) return stopHold();
      rail.scrollTop = clamp(rail.scrollTop + dir * HOLD_SPEED, 0, rail.scrollHeight - rail.clientHeight);
      syncThumb();
    }, HOLD_INTERVAL);
  }
  function stopHold(){ if (holdTimer){ clearInterval(holdTimer); holdTimer=null; } }

  // drag
  function onThumbDown(e){
    dragging = true;
    dragStartY = (e.touches ? e.touches[0].clientY : e.clientY);
    dragStartScroll = rail.scrollTop;
    document.addEventListener('mousemove', onThumbMove);
    document.addEventListener('touchmove', onThumbMove, { passive:false });
    document.addEventListener('mouseup', onThumbUp);
    document.addEventListener('touchend', onThumbUp);
    e.preventDefault();
  }
  function onThumbMove(e){
    if (!dragging) return;
    const y = (e.touches ? e.touches[0].clientY : e.clientY);
    const dy = y - dragStartY;

    const innerUsable = Math.max(track.clientHeight - RAIL_INSET*2, 0);
    const thumbH = thumb.getBoundingClientRect().height;
    const maxY = Math.max(innerUsable - thumbH, 0);
    const maxScroll = Math.max(rail.scrollHeight - rail.clientHeight, 0);
    const scrollDelta = maxY ? (dy / maxY) * maxScroll : 0;

    rail.scrollTop = clamp(dragStartScroll + scrollDelta, 0, maxScroll);
    syncThumb();
    e.preventDefault();
  }
  function onThumbUp(){
    dragging = false;
    document.removeEventListener('mousemove', onThumbMove);
    document.removeEventListener('touchmove', onThumbMove);
    document.removeEventListener('mouseup', onThumbUp);
    document.removeEventListener('touchend', onThumbUp);
  }

  function mount(){
    if (mounted) return; mounted = true;

    rail.addEventListener('scroll', syncThumb, { passive:true });
    up.addEventListener('click',   () => scrollBy(-STEP));
    down.addEventListener('click', () => scrollBy( STEP));
    ['mousedown','touchstart'].forEach(evt => up.addEventListener(evt, () => startHold(-1), { passive:true }));
    ['mousedown','touchstart'].forEach(evt => down.addEventListener(evt, () => startHold( 1), { passive:true }));
    ['mouseleave','mouseup','touchend','touchcancel'].forEach(evt => { up.addEventListener(evt, stopHold); down.addEventListener(evt, stopHold); });

    rail.tabIndex = 0;
    rail.addEventListener('keydown', (e)=>{
      if (e.key === 'ArrowUp'){ e.preventDefault(); scrollBy(-STEP); }
      if (e.key === 'ArrowDown'){ e.preventDefault(); scrollBy( STEP); }
      if (e.key === 'PageUp'){ e.preventDefault(); scrollBy(-rail.clientHeight*.9); }
      if (e.key === 'PageDown'){ e.preventDefault(); scrollBy( rail.clientHeight*.9); }
    });

    // observe size/content changes
    ro = new ResizeObserver(syncThumb);
    ro.observe(rail);

    requestAnimationFrame(syncThumb);
    setTimeout(syncThumb, 250);
  }
  function unmount(){
    if (!mounted) return; mounted = false;
    stopHold();
    rail.removeEventListener('scroll', syncThumb);
    if (ro){ ro.disconnect(); ro = null; }
  }

  function evaluate(){
    if (window.matchMedia(BREAKPOINT).matches) mount(); else unmount();
  }
  const mq = window.matchMedia(BREAKPOINT);
  mq.addEventListener ? mq.addEventListener('change', evaluate) : mq.addListener(evaluate);
  evaluate();
})();

